import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { taxFormsService } from '../../services';
import './TaxForms.css';

const W8BENForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    // Part I - Identification of Beneficial Owner
    name: '',
    countryOfCitizenship: '',
    permanentAddress: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: '',
    mailingAddress: '',
    mailingCity: '',
    mailingStateProvince: '',
    mailingPostalCode: '',
    mailingCountry: '',
    
    // Part II - Claim of Tax Treaty Benefits
    usTaxpayerIdNumber: '',
    foreignTaxIdNumber: '',
    referenceNumber: '',
    dateOfBirth: '',
    
    // Part III - Certification
    signature: '',
    signatureDate: new Date().toISOString().split('T')[0],
    printName: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    const requiredFields = [
      'name', 'countryOfCitizenship', 'permanentAddress', 
      'city', 'country', 'dateOfBirth', 'signature', 'printName'
    ];
    
    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'This field is required';
      }
    });
    
    // Date validation
    if (formData.dateOfBirth && new Date(formData.dateOfBirth) > new Date()) {
      newErrors.dateOfBirth = 'Date of birth cannot be in the future';
    }
    
    // Signature validation
    if (formData.signature && formData.signature !== formData.printName) {
      newErrors.signature = 'Signature must match printed name for digital signature';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await taxFormsService.submitTaxForm({
        formType: 'W-8BEN',
        formData: formData
      });
      
      // Mark tax form as completed and redirect to dashboard
      navigate('/agent/dashboard');
    } catch (error) {
      console.error('Error submitting W-8BEN:', error);
      setErrors({
        submit: error.response?.data?.message || 'Failed to submit tax form. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="tax-form-container">
      <div className="tax-form-header">
        <h1>Form W-8BEN</h1>
        <p>Certificate of Foreign Status of Beneficial Owner for United States Tax Withholding and Reporting (Individuals)</p>
        <div className="alert alert-info">
          This form is required for international agents to establish foreign status and claim tax treaty benefits.
        </div>
      </div>

      <form onSubmit={handleSubmit} className="tax-form">
        {/* Part I - Identification of Beneficial Owner */}
        <section className="form-section">
          <h2>Part I - Identification of Beneficial Owner</h2>
          
          <div className="form-group">
            <label htmlFor="name">1. Name of individual who is the beneficial owner *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={errors.name ? 'error' : ''}
              required
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="countryOfCitizenship">2. Country of citizenship *</label>
            <input
              type="text"
              id="countryOfCitizenship"
              name="countryOfCitizenship"
              value={formData.countryOfCitizenship}
              onChange={handleChange}
              className={errors.countryOfCitizenship ? 'error' : ''}
              required
            />
            {errors.countryOfCitizenship && <span className="error-message">{errors.countryOfCitizenship}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="permanentAddress">3. Permanent residence address *</label>
            <input
              type="text"
              id="permanentAddress"
              name="permanentAddress"
              value={formData.permanentAddress}
              onChange={handleChange}
              className={errors.permanentAddress ? 'error' : ''}
              placeholder="Street, apt. or suite no., or rural route"
              required
            />
            {errors.permanentAddress && <span className="error-message">{errors.permanentAddress}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">City or town *</label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                className={errors.city ? 'error' : ''}
                required
              />
              {errors.city && <span className="error-message">{errors.city}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="stateProvince">State or province</label>
              <input
                type="text"
                id="stateProvince"
                name="stateProvince"
                value={formData.stateProvince}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="postalCode">Postal code</label>
              <input
                type="text"
                id="postalCode"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="country">Country *</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className={errors.country ? 'error' : ''}
              required
            />
            {errors.country && <span className="error-message">{errors.country}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="mailingAddress">4. Mailing address (if different from above)</label>
            <input
              type="text"
              id="mailingAddress"
              name="mailingAddress"
              value={formData.mailingAddress}
              onChange={handleChange}
              placeholder="Street, apt. or suite no., or rural route"
            />
          </div>

          {formData.mailingAddress && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="mailingCity">City or town</label>
                <input
                  type="text"
                  id="mailingCity"
                  name="mailingCity"
                  value={formData.mailingCity}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="mailingStateProvince">State or province</label>
                <input
                  type="text"
                  id="mailingStateProvince"
                  name="mailingStateProvince"
                  value={formData.mailingStateProvince}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="mailingPostalCode">Postal code</label>
                <input
                  type="text"
                  id="mailingPostalCode"
                  name="mailingPostalCode"
                  value={formData.mailingPostalCode}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="mailingCountry">Country</label>
                <input
                  type="text"
                  id="mailingCountry"
                  name="mailingCountry"
                  value={formData.mailingCountry}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="usTaxpayerIdNumber">5. U.S. taxpayer identification number (SSN or ITIN), if any</label>
              <input
                type="text"
                id="usTaxpayerIdNumber"
                name="usTaxpayerIdNumber"
                value={formData.usTaxpayerIdNumber}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="foreignTaxIdNumber">6. Foreign tax identifying number</label>
              <input
                type="text"
                id="foreignTaxIdNumber"
                name="foreignTaxIdNumber"
                value={formData.foreignTaxIdNumber}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="referenceNumber">7. Reference number(s)</label>
              <input
                type="text"
                id="referenceNumber"
                name="referenceNumber"
                value={formData.referenceNumber}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="dateOfBirth">8. Date of birth (MM-DD-YYYY) *</label>
              <input
                type="date"
                id="dateOfBirth"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className={errors.dateOfBirth ? 'error' : ''}
                required
              />
              {errors.dateOfBirth && <span className="error-message">{errors.dateOfBirth}</span>}
            </div>
          </div>
        </section>

        {/* Part III - Certification */}
        <section className="form-section">
          <h2>Part III - Certification</h2>
          
          <div className="certification-text">
            <p>
              Under penalties of perjury, I declare that I have examined the information on this form and to the best of my knowledge and belief it is true, correct, and complete. I further certify under penalties of perjury that:
            </p>
            <ul>
              <li>I am the beneficial owner (or am authorized to sign for the beneficial owner) of all the income to which this form relates</li>
              <li>The beneficial owner is not a U.S. person</li>
              <li>The income to which this form relates is not effectively connected with the conduct of a trade or business in the United States</li>
              <li>For broker transactions or barter exchanges, the beneficial owner is an exempt foreign person as defined in the instructions</li>
            </ul>
          </div>

          <div className="form-group">
            <label htmlFor="printName">Print name of signer *</label>
            <input
              type="text"
              id="printName"
              name="printName"
              value={formData.printName}
              onChange={handleChange}
              className={errors.printName ? 'error' : ''}
              required
            />
            {errors.printName && <span className="error-message">{errors.printName}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="signature">Sign Here (Type your full name as digital signature) *</label>
            <input
              type="text"
              id="signature"
              name="signature"
              value={formData.signature}
              onChange={handleChange}
              className={errors.signature ? 'error' : ''}
              placeholder="Type your full name exactly as printed above"
              required
            />
            {errors.signature && <span className="error-message">{errors.signature}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="signatureDate">Date *</label>
            <input
              type="date"
              id="signatureDate"
              name="signatureDate"
              value={formData.signatureDate}
              onChange={handleChange}
              required
              readOnly
            />
          </div>
        </section>

        {errors.submit && (
          <div className="alert alert-danger">{errors.submit}</div>
        )}

        <div className="form-actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit W-8BEN Form'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default W8BENForm;